import { create } from 'zustand';
import React from 'react';
import {
  PATHS,
  SCAN_PROCESS_STEPS,
  WORKFLOW_TYPES,
  MAIN_PROCESS_STEPS,
  WORKFLOW_CONFIG,
  GLOBAL_STEP_ORDER,
  normalizeWorkflowType,
  DESIGN_WORKFLOW_TYPES,
  SCAN_PROCESS_FLOW_STEPS,
} from '../constants/flowConfig';
import { TOOLS_CONFIG, getToolIdsByOrder } from '../constants/tools';
import {
  fetchOrderData,
  mapOrderToPatientData,
  fetchScanFilesMeta,
  persistBkDesignSourceMetadata,
  uploadSourceFiles,
  getBkErrorMessage,
  recordBkDesignScanFinalPreviewComplete,
} from '../services/erpApiService';
import useEditorStore from './useEditorStore';
import {
  resolveBkDesignOrderContext,
  saveBkDesignOrderContext,
} from '../lib/bkDesignOrderContext';
import { uploadScanFinalPreviewCheckpointFromEditor } from '../lib/bkCheckpointUpload';
import { buildStepCheckpointMetadata } from '../lib/stepCheckpointUpload';

/** Deduplicates concurrent persist calls for the same order (Strict Mode / overlapping effects). */
const scanFinalPersistPromiseByOrderKey = new Map();

function buildPreparingScanMetadata() {
  const { modelManualScale, modelScaleUnit } = useEditorStore.getState();
  return {
    schema_version: 1,
    preparing_scan: {
      manual_scale_adjustment: modelManualScale,
      model_scale_unit: modelScaleUnit,
    },
  };
}

// Step configurations with labels and descriptions
// The circumference-lines measuring tool is defined on Rectification (it seeds
// from the applied circumference modification) but stays useful for the rest of
// the flow, so every main step that carries Measure / Circumference lists it too.
const CIRCUMFERENCE_LINES_TOOL_ID =
  TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_circumference_lines.id;

export const STEP_CONFIGS = {
  [SCAN_PROCESS_STEPS.PREPARING_SCAN]: {
    label: 'Preparing Scan',
    description: 'Clean and prepare the scanned 3D model',
    tools: [
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.TRIM.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.LASSO.id,
      // TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.ERASE.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.FILL.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.MOVE.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.ROTATE.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.SMOOTH.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.GLOBAL_SMOOTH.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.MEASURE.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.CIRCUMFERENCE.id,
    ]
  },
  [SCAN_PROCESS_STEPS.AXES_ALIGNMENT]: {
    label: 'Axes & Alignment',
    description: 'Align the model axes for proper orientation',
    tools: [TOOLS_CONFIG.SCAN_PROCESS.AXES_ALIGNMENT.AXES.id]
  },
  [SCAN_PROCESS_STEPS.FINAL_DESIGN_PREVIEW]: {
    label: 'Preview',
    description: 'Review the prepared scan before clinical modification',
    tools: []
  },
  [MAIN_PROCESS_STEPS.RECTIFICATION]: {
    label: 'Rectification',
    description: 'Apply rectification to the socket design',
    tools: [
      // Rectification's own list already carries CIRCUMFERENCE_LINES_TOOL_ID
      // (it is declared in that step's tool config).
      ...getToolIdsByOrder(TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION),
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.MEASURE.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.CIRCUMFERENCE.id
    ]
  },
  [MAIN_PROCESS_STEPS.ELONGATION]: {
    label: 'Elongation',
    description: 'Extend the socket design as needed',
    tools: [
      'main_elongation_elongation',
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.MEASURE.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.CIRCUMFERENCE.id,
      CIRCUMFERENCE_LINES_TOOL_ID
    ]
  },
  [MAIN_PROCESS_STEPS.TRIMLINE]: {
    label: 'Trimline',
    description: 'Define trimlines for the socket',
    tools: [
      'main_trimline_trimline',
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.MEASURE.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.CIRCUMFERENCE.id,
      CIRCUMFERENCE_LINES_TOOL_ID
    ]
  },
  [MAIN_PROCESS_STEPS.ML_REDUCTION]: {
    label: 'ML Reduction',
    description: 'Select two regions for medio-lateral reduction',
    tools: [
      TOOLS_CONFIG.MAIN_PROCESS.ML_REDUCTION.main_ml_reduction_brush.id,
      TOOLS_CONFIG.MAIN_PROCESS.ML_REDUCTION.main_ml_reduction_smoothning.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.MEASURE.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.CIRCUMFERENCE.id,
      CIRCUMFERENCE_LINES_TOOL_ID
    ]
  },
  [MAIN_PROCESS_STEPS.FLARES]: {
    label: 'Flares',
    description: 'Add flares to the socket design',
    tools: [
      TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_flare.id,
      TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_smoothning.id,
      TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_global_smooth.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.MEASURE.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.CIRCUMFERENCE.id,
      CIRCUMFERENCE_LINES_TOOL_ID
    ]
  },
  [MAIN_PROCESS_STEPS.THICKNESS]: {
    label: 'Thickness',
    description: 'Set wall thickness for the socket',
    tools: [
      'main_thickness_thickness',
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.MEASURE.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.CIRCUMFERENCE.id,
      CIRCUMFERENCE_LINES_TOOL_ID
    ]
  },
  [MAIN_PROCESS_STEPS.CLINICAL_MODIFICATION_PREVIEW]: {
    label: 'Preview',
    description: 'Review clinical modifications before alignment and locking',
    tools: []
  },
  [MAIN_PROCESS_STEPS.LOCKING_MECHANISM]: {
    label: 'Locking Mechanism',
    description: 'Align socket to reference ladder, then add locking mechanism',
    tools: [
      TOOLS_CONFIG.MAIN_PROCESS.LOCKING_MECHANISM.main_locking_lock.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.MEASURE.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.CIRCUMFERENCE.id,
      CIRCUMFERENCE_LINES_TOOL_ID
    ]
  },

  [MAIN_PROCESS_STEPS.VALVE_PATTERN]: {
    label: 'Valve pattern',
    description: 'Project and adjust the valve pattern on the socket',
    tools: [
      ...getToolIdsByOrder(TOOLS_CONFIG.MAIN_PROCESS.VALVE_PATTERN),
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.MEASURE.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.CIRCUMFERENCE.id,
      CIRCUMFERENCE_LINES_TOOL_ID
    ]
  },

  [MAIN_PROCESS_STEPS.VALVE]: {
    label: 'Valve',
    description: 'Place an auto expulsion valve on the aligned socket',
    tools: [
      'main_valve_valve',
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.MEASURE.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.CIRCUMFERENCE.id,
      CIRCUMFERENCE_LINES_TOOL_ID
    ]
  },
  [MAIN_PROCESS_STEPS.LOGO_PLACEMENT]: {
    label: 'Logo Placement',
    description: 'Place the brand logo on the socket surface',
    tools: [
      TOOLS_CONFIG.MAIN_PROCESS.LOGO_PLACEMENT.main_logo_placement.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.MEASURE.id,
      TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.CIRCUMFERENCE.id,
      CIRCUMFERENCE_LINES_TOOL_ID
    ]
  },
  // [MAIN_PROCESS_STEPS.PADS]: {
  //   label: 'Pads',
  //   description: 'Add comfort pads to the socket',
  //   tools: [
  //     'main_pads_pad',
  //     TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.MEASURE.id,
  //     TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.CIRCUMFERENCE.id
  //   ]
  // },
  [MAIN_PROCESS_STEPS.FINALIZATION]: {
    label: 'Finalization',
    description: 'Final review and completion',
    tools: ['main_finalization_review', 'main_finalization_export', 'main_finalization_save']
  },
  // Add config for the new tool
  main_rectification_smoothning: {
    label: 'Smoothning',
    description: 'Smooth the socket surface',
    icon: '/tool_icons/scan_preparing_eraser_smoothning.png',
    // Use the same tool card as scoop/recharge
  },
  main_rectification_carve_smoothning: {
    label: 'Smoothing',
    description: 'Blend the transitions left by carve relief',
    icon: '/tool_icons/scan_preparing_eraser_smoothning.png',
  },
  main_flares_flare: {
    label: 'Flare',
    description: 'Configure flare segments',
    icon: '/tool_icons/main_flares_flares.png',
  },
  main_flares_smoothning: {
    label: 'Flares Smoothening',
    description: 'Smoothen flare deformations',
    icon: '/tool_icons/scan_preparing_eraser_smoothning.png',
  },
  main_ml_reduction_smoothning: {
    label: 'ML Reduction Smoothing',
    description: 'Smooth ML reduction transitions',
    icon: '/tool_icons/scan_preparing_eraser_smoothning.png',
  },
  main_trimline_trimline: {
    label: 'Trimline',
    description: 'Define trimlines for the socket',
    icon: '/tool_icons/main_trimline_trimline.png',
  },
  main_thickness_thickness: {
    label: 'Thickness',
    description: 'Set wall thickness for the socket',
    icon: '/tool_icons/main_thickness_thickness.png',
  }
};

const useWorkflowStore = create((set, get) => ({
  // ===== STATE =====
  // Current workflow and step
  currentWorkflow: WORKFLOW_TYPES.SCAN_PROCESS,
  currentStep: SCAN_PROCESS_STEPS.PREPARING_SCAN,
  
  // Patient data
  patientData: null,
  rawOrderData: null,
  isLoadingPatientData: false,
  patientDataError: null,

  // Scan files
  scanFilesMeta: null,
  isFetchingScanFiles: false,
  isUploadingScanFiles: false,
  scanAutoLoaded: false,

  /**
   * After a successful scan-final preview ERP persist on Rectification step enter
   * (`sales_order:customer`). Cleared when `fetchPatientData` switches orders.
   */
  scanFinalPreviewPersistedForOrderKey: null,
  bkDesignOrderKey: null,
  /**
   * Explicit intent for how the user entered Main Process.
   * Used to decide whether to hydrate checkpoints from the API (and for which step),
   * while avoiding overwriting in-memory edits on back/forward navigation.
   *
   * - 'fresh': redesign from scratch (Scan Process → Main Process) → do NOT auto-fetch rectification/locking checkpoints
   * - 'modify_rectification': user clicked Modify Rectification in Finalization → hydrate rectification checkpoint once
   * - 'modify_locking': user clicked Modify Locking in Finalization → hydrate locking checkpoint once
   */
  bkMainProcessIntent: 'modify_rectification',

  /** Hydrate-once guards to prevent repeat API overwrites during navigation */
  bkCheckpointHydrated: {
    rectification: false,
    locking: false,
  },

  /** True while a server checkpoint mesh is being fetched/loaded — keeps step loaders up. */
  bkCheckpointHydrating: false,

  // Socket type selection modal  
  showSocketTypeModal: false,
  selectedSocketType: 'definitive_socket',
  
  // Step completion tracking
  stepCompletion: {
    // Scan process steps
    [SCAN_PROCESS_STEPS.PREPARING_SCAN]: false,
    [SCAN_PROCESS_STEPS.AXES_ALIGNMENT]: false,
    [SCAN_PROCESS_STEPS.FINAL_DESIGN_PREVIEW]: false,
    
    // Main process steps
    [MAIN_PROCESS_STEPS.RECTIFICATION]: false,
    [MAIN_PROCESS_STEPS.ELONGATION]: false,
    [MAIN_PROCESS_STEPS.TRIMLINE]: false,
    [MAIN_PROCESS_STEPS.ML_REDUCTION]: false,
    [MAIN_PROCESS_STEPS.FLARES]: false,
    [MAIN_PROCESS_STEPS.THICKNESS]: false,
    [MAIN_PROCESS_STEPS.CLINICAL_MODIFICATION_PREVIEW]: false,
    [MAIN_PROCESS_STEPS.LOCKING_MECHANISM]: false,
    [MAIN_PROCESS_STEPS.VALVE]: false,
    [MAIN_PROCESS_STEPS.LOGO_PLACEMENT]: false,
    [MAIN_PROCESS_STEPS.PADS]: false,
    [MAIN_PROCESS_STEPS.VALVE_PATTERN]: false,

    [MAIN_PROCESS_STEPS.FINALIZATION]: false,
  },

  // ===== ACTIONS =====
  actions: {
    // --- WORKFLOW NAVIGATION ---
    setCurrentWorkflow: (workflow) => {
      set({ currentWorkflow: workflow });
    },

    setCurrentStep: (step) => {
      set({ currentStep: step });
    },

    setBkMainProcessIntent: (intent, opts = { resetHydrated: true }) => {
      set((state) => ({
        bkMainProcessIntent: intent,
        ...(opts?.resetHydrated
          ? { bkCheckpointHydrated: { rectification: false, locking: false } }
          : {}),
      }));
    },

    markBkCheckpointHydrated: (stepKey) => {
      set((state) => ({
        bkCheckpointHydrated: { ...state.bkCheckpointHydrated, [stepKey]: true },
      }));
    },

    setBkCheckpointHydrating: (hydrating) => set({ bkCheckpointHydrating: !!hydrating }),

    // Navigate to next step
    goToNextStep: () => {
      const { currentWorkflow, currentStep, stepCompletion } = get();
      
      if (currentWorkflow === WORKFLOW_TYPES.SCAN_PROCESS) {
        const scanSteps = SCAN_PROCESS_FLOW_STEPS;
        const currentIndex = scanSteps.indexOf(currentStep);
        
        if (currentIndex < scanSteps.length - 1) {
          const nextStep = scanSteps[currentIndex + 1];
          set({ currentStep: nextStep });
        } else {
          set({
            currentWorkflow: WORKFLOW_TYPES.CLINICAL_MODIFICATION,
            currentStep: MAIN_PROCESS_STEPS.RECTIFICATION,
            bkMainProcessIntent: 'fresh',
            bkCheckpointHydrated: { rectification: false, locking: false },
          });
        }
      } else if (DESIGN_WORKFLOW_TYPES.includes(normalizeWorkflowType(currentWorkflow, currentStep))) {
        const wf = normalizeWorkflowType(currentWorkflow, currentStep);
        const steps = WORKFLOW_CONFIG[wf]?.steps ?? [];
        const currentIndex = steps.indexOf(currentStep);

        if (currentIndex < steps.length - 1 && currentIndex >= 0) {
          set({ currentStep: steps[currentIndex + 1] });
        } else if (WORKFLOW_CONFIG[wf]?.nextWorkflow) {
          const { workflow, step } = WORKFLOW_CONFIG[wf].nextWorkflow;
          set({ currentWorkflow: workflow, currentStep: step });
        }
      }
    },

    // Navigate to previous step
    goToPreviousStep: () => {
      set((state) => {
        const { currentWorkflow, currentStep } = state;
        const config = WORKFLOW_CONFIG[normalizeWorkflowType(currentWorkflow, currentStep)];
    
        if (!config) return {}; // Safety check, do nothing if workflow is unknown
    
        const currentIndex = config.steps.indexOf(currentStep);
    
        if (currentIndex > 0) {
          // Case 1: Move to the previous step within the SAME workflow.
          const prevStep = config.steps[currentIndex - 1];
          return { currentStep: prevStep };
        } else if (config.previousWorkflow) {
          // Case 2: We're at the first step, so move to the PREVIOUS workflow.
          return {
            currentWorkflow: config.previousWorkflow.workflow,
            currentStep: config.previousWorkflow.step,
          };
        }
    
        // Case 3: At the very first step of the entire process. Do nothing.
        return {};
      });
    },

    // --- STEP COMPLETION ---
    setStepCompletion: (step, completed = true) => {
      set(state => ({
        stepCompletion: {
          ...state.stepCompletion,
          [step]: completed
        }
      }));
    },

    // Complete current step and move to next
    completeCurrentStep: () => {
      const { currentStep, currentWorkflow, actions } = get();
      
      // 1. Mark the current step as complete
      actions.setStepCompletion(currentStep, true);
    
      // 2. Check for and execute any special handlers for this step
      const wf = normalizeWorkflowType(currentWorkflow, currentStep);
      const handler = WORKFLOW_CONFIG[wf]?.stepHandlers?.[currentStep]?.onComplete;
      if (handler) {
        const result = handler(set);
        // If the handler took over (e.g., to show a modal), stop here.
        if (result?.handled) return null;
      }
    
      // 3. Determine the next step using the config
      const config = WORKFLOW_CONFIG[wf];
      if (!config) return null;
      const currentIndex = config.steps.indexOf(currentStep);
      let nextState = {};

      // Step removed from workflow but user may still open legacy `/main/alignment` route
      if (currentIndex < 0 && currentStep === MAIN_PROCESS_STEPS.ALIGNMENT) {
        return PATHS[MAIN_PROCESS_STEPS.LOCKING_MECHANISM];
      }
    
      if (currentIndex < config.steps.length - 1) {
        // Case A: Next step is in the same workflow
        const nextStep = config.steps[currentIndex + 1];
        nextState = { currentStep: nextStep };
      } else if (config.nextWorkflow) {
        // Case B: Transition to the next workflow
        nextState = {
          currentWorkflow: config.nextWorkflow.workflow,
          currentStep: config.nextWorkflow.step,
        };
      } else {
        // Case C: End of all workflows
        console.log("All workflow steps completed.");
        return null; // Or return a path to a summary page
      }
    
      if (nextState.currentStep) {
        set(nextState);
      }

      return PATHS[nextState.currentStep];
    },

    // Socket type selection modal actions
    showSocketTypeSelectionModal: () => {
      set({ showSocketTypeModal: true });
    },

    hideSocketTypeSelectionModal: () => {
      set({ showSocketTypeModal: false });
    },

    setSelectedSocketType: (socketType) => {
      set({ selectedSocketType: socketType });
    },

    handleSocketTypeContinue: (socketType) => {
      const { actions } = get();
      set({ 
        selectedSocketType: socketType,
        showSocketTypeModal: false 
      });
      
      // Flares onComplete showed the modal; after confirm, go to Thickness (ML Reduction is already done)
      actions.setStepCompletion(MAIN_PROCESS_STEPS.FLARES, true);
      set({ currentStep: MAIN_PROCESS_STEPS.THICKNESS });

      return PATHS[MAIN_PROCESS_STEPS.THICKNESS];
    },

    // --- PATIENT DATA ---
    fetchPatientData: async (salesOrder, customer) => {
      const resolved = resolveBkDesignOrderContext(salesOrder, customer);
      if (!resolved) {
        console.error(
          'BK design: missing sales_order and customer. Open the order from BengaitLabs with ?sales_order=…&customer=… once, or ensure localStorage context exists.'
        );
        set({
          isLoadingPatientData: false,
          patientDataError:
            'Order context missing (sales_order/customer). Please reopen this design from BengaitLabs.',
        });
        throw new Error('BK design order context missing');
      }
      const { sales_order: so, customer: cu } = resolved;
      saveBkDesignOrderContext(so, cu);

      const orderKey = `${so}:${cu}`;
      const prevKey = get().bkDesignOrderKey;
      if (orderKey !== prevKey) {
        set({
          bkDesignOrderKey: orderKey,
          bkMainProcessIntent: 'modify_rectification',
          bkCheckpointHydrated: { rectification: false, locking: false },
          /** Allow `useBkScanAutoLoad` to pull mesh for the new SO (was true for previous order). */
          scanAutoLoaded: false,
          scanFinalPreviewPersistedForOrderKey: null,
        });
      }

      set({
        isLoadingPatientData: true,
        isFetchingScanFiles: true,
        patientDataError: null,
      });
      try {
        const [orderSettled, scanSettled] = await Promise.allSettled([
          fetchOrderData(so, cu),
          fetchScanFilesMeta(so, cu),
        ]);

        const patch = { isLoadingPatientData: false, isFetchingScanFiles: false };
        let orderError = null;

        if (orderSettled.status === 'fulfilled') {
          const apiResponse = orderSettled.value;
          patch.patientData = mapOrderToPatientData(apiResponse);
          patch.rawOrderData = apiResponse;
          patch.patientDataError = null;
        } else {
          console.error('Error fetching order / patient data:', orderSettled.reason);
          orderError = orderSettled.reason;
          patch.patientData = null;
          patch.rawOrderData = null;
          patch.patientDataError = getBkErrorMessage(orderSettled.reason);
        }

        if (scanSettled.status === 'fulfilled') {
          patch.scanFilesMeta = scanSettled.value;
        } else {
          console.error('Error fetching BK design files meta:', scanSettled.reason);
        }

        set(patch);
        if (orderError) throw orderError;
        return patch.patientData;
      } catch (error) {
        console.error('Error fetching patient data:', error);
        set({
          isLoadingPatientData: false,
          isFetchingScanFiles: false,
          patientDataError: getBkErrorMessage(error),
        });
        throw error;
      }
    },

    // --- SCAN FILES ---
    fetchScanFiles: async (salesOrderId, customer) => {
      const resolved = resolveBkDesignOrderContext(salesOrderId, customer);
      if (!resolved) {
        console.error('BK design: cannot fetch scan files — missing order context');
        return null;
      }
      const { sales_order: so, customer: cu } = resolved;
      saveBkDesignOrderContext(so, cu);

      set({ isFetchingScanFiles: true });
      try {
        const meta = await fetchScanFilesMeta(so, cu);
        set({ scanFilesMeta: meta, isFetchingScanFiles: false });
        return meta;
      } catch (error) {
        console.error('Error fetching scan files:', error);
        set({ isFetchingScanFiles: false });
        return null;
      }
    },

    setScanAutoLoaded: (value) => set({ scanAutoLoaded: value }),

    clearScanFinalPreviewPersistState: () =>
      set({
        scanFinalPreviewPersistedForOrderKey: null,
      }),

    /**
     * Persist scan final preview mesh (presign→finalize) or milestone-only record — once per order.
     * Primary trigger: `/design/scan/final-preview` mount. Rectification-on-load remains a fallback
     * when this step was skipped (deep links).
     * Prefer passing {@link resolveBkDesignOrderContext}-compatible fields from URL search params
     * (`sales_order`, `customer`) plus localStorage fallback.
     * @param {{ sales_order?: string, customer?: string }} [explicitOpt]
     */
    persistScanFinalPreviewToErpIfNeeded: async (explicitOpt = {}) => {
      const ctx = resolveBkDesignOrderContext(
        explicitOpt.sales_order ?? explicitOpt.salesOrderId ?? null,
        explicitOpt.customer ?? null,
      );
      if (!ctx) {
        return { ok: false, skipped: true, reason: 'no_context' };
      }
      saveBkDesignOrderContext(ctx.sales_order, ctx.customer);
      const key = `${ctx.sales_order}:${ctx.customer}`;

      if (get().scanFinalPreviewPersistedForOrderKey === key) {
        return { ok: true, skipped: true, reason: 'already_persisted' };
      }

      const existing = scanFinalPersistPromiseByOrderKey.get(key);
      if (existing) {
        return existing;
      }

      const run = (async () => {
        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

        // Ensure DesignLayout.fetchPatientData + scan meta have finished before upload (deep links).
        const hydrateDeadline = Date.now() + 20000;
        while (Date.now() < hydrateDeadline) {
          const { isLoadingPatientData, isFetchingScanFiles } = get();
          if (!isLoadingPatientData && !isFetchingScanFiles) break;
          await sleep(160);
        }

        let mesh = useEditorStore.getState().sceneRefs?.shadedMesh;
        const meshDeadline = Date.now() + 45000;
        const meshReady = (m) =>
          !!m?.geometry?.attributes?.position?.count;
        while (!meshReady(mesh) && Date.now() < meshDeadline) {
          await sleep(120);
          mesh = useEditorStore.getState().sceneRefs?.shadedMesh;
        }

        try {
          if (meshReady(mesh)) {
            await uploadScanFinalPreviewCheckpointFromEditor(
              mesh,
              ctx.sales_order,
              ctx.customer,
              buildStepCheckpointMetadata(SCAN_PROCESS_STEPS.AXES_ALIGNMENT, {
                format: 'ply',
              }),
            );
            await get().actions.fetchScanFiles(ctx.sales_order, ctx.customer);
          } else {
            if (import.meta.env.DEV) {
              console.warn(
                'persistScanFinalPreview: no shaded mesh with geometry yet; recording milestone-only (no presigned upload).',
              );
            }
            await recordBkDesignScanFinalPreviewComplete({
              salesOrderId: ctx.sales_order,
              customer: ctx.customer,
            });
          }
          set({ scanFinalPreviewPersistedForOrderKey: key });
          return { ok: true, skipped: false };
        } catch (err) {
          console.error('persistScanFinalPreviewToErpIfNeeded:', err);
          return { ok: false, skipped: false, error: err };
        } finally {
          scanFinalPersistPromiseByOrderKey.delete(key);
        }
      })();

      scanFinalPersistPromiseByOrderKey.set(key, run);
      return run;
    },

    /**
     * Persist preparing-scan fields (manual scale, unit) without uploading meshes.
     */
    persistPreparingScanMetadata: async () => {
      const resolved = resolveBkDesignOrderContext(null, null);
      if (!resolved) {
        console.error('BK design: cannot persist preparing-scan metadata — missing order context');
        return false;
      }
      const { sales_order: so, customer: cu } = resolved;
      await persistBkDesignSourceMetadata(so, cu, buildPreparingScanMetadata());
      return true;
    },

    uploadAndProceed: async (salesOrderId, customer, files) => {
      const resolved = resolveBkDesignOrderContext(salesOrderId, customer);
      if (!resolved) {
        console.error('BK design: cannot upload — missing order context');
        return false;
      }
      const { sales_order: so, customer: cu } = resolved;
      saveBkDesignOrderContext(so, cu);

      set({ isUploadingScanFiles: true });
      try {
        await uploadSourceFiles(so, cu, files, buildPreparingScanMetadata());
        let meta = null;
        try {
          meta = await fetchScanFilesMeta(so, cu);
        } catch (e) {
          console.error('Error refreshing files after upload:', e);
        }
        set({
          isUploadingScanFiles: false,
          ...(meta != null ? { scanFilesMeta: meta, scanAutoLoaded: false } : {}),
        });
        return true;
      } catch (error) {
        console.error('Error uploading scan files:', error);
        set({ isUploadingScanFiles: false });
        return false;
      }
    },

    // --- UTILITY FUNCTIONS ---
    getCurrentStepConfig: () => {
      const { currentStep } = get();
      return STEP_CONFIGS[currentStep] || {};
    },

    getCurrentWorkflowSteps: () => {
      const { currentWorkflow } = get();
      // Keep the stepper UI in sync with the actual navigation order
      // (WORKFLOW_CONFIG.steps). This also allows temporary skipping
      // of steps without duplicating step lists here.
      const config = WORKFLOW_CONFIG[normalizeWorkflowType(currentWorkflow)];
      if (config?.steps?.length) return config.steps;
      if (currentWorkflow === WORKFLOW_TYPES.SCAN_PROCESS) return SCAN_PROCESS_FLOW_STEPS;
      return [];
    },

    canNavigateToStep: (step) => {
      const { stepCompletion } = get();
      const stepIndex = GLOBAL_STEP_ORDER.indexOf(step);
      if (stepIndex < 0) return false;
      if (stepIndex === 0) return true;
      return GLOBAL_STEP_ORDER.slice(0, stepIndex).every((s) => stepCompletion[s]);
    },

    isStepCompleted: (step) => {
      const { stepCompletion } = get();
      return stepCompletion[step] || false;
    },

    isCurrentStepCompleted: () => {
      const { currentStep, stepCompletion } = get();
      return stepCompletion[currentStep] || false;
    }
  },
}));

export const useWorkflowState = (selector, equalityFn) => useWorkflowStore(selector, equalityFn);
export const useWorkflowActions = () => useWorkflowStore((state) => state.actions);

export default useWorkflowStore;